import { combineReducers, createSlice, type UnknownAction } from '@reduxjs/toolkit';

import { cartECouponAdapter } from '../../adapters/cartECouponAdapters';
import { productsAdapter } from '../../adapters/productsAdapter';
import { recipesAdapter } from '../../adapters/recipesAdapter';
import cartLoadingReducer, {
    getInitialState as getCartLoadingInitialState,
} from './cartApiStateSlice';
import { cartECouponReducer } from './cartECouponSlice';
import { cartEditedOrderItemsReducer } from './cartEditedOrderItemsSlice';
import {
    cartEditOrderReducer,
    getInitialState as getCartEditOrderInitialState,
} from './cartEditOrderSlice';
import cartItemsProductDataReducer from './cartItemsProductDataSlice';
import cartItemsReducer from './cartItemsSlice';
import cartPromosReducer from './cartPromosSlice';
import cartRecipesReducer from './cartRecipesSlice';
import { cartSectionsReducer } from './cartSectionsSlice';
import cartSummaryReducer, {
    getInitialState as getCartSummaryInitialState,
} from './cartSummarySlice';
import {
    cartTimeslotReducer,
    getInitialState as getTimeslotInitialState,
} from './cartTimeslotSlice';

// we cannot have direct object prop on combineReducers, so we create a simple reducer that just returns the state to hold the field
// the action.type is for non dependency cycle
const isSetFullCartAction = (
    action: UnknownAction,
): action is UnknownAction & { payload: { commentAboutCustomer: string | null } } => {
    return action.type === 'cart/setFullCart';
};

const commentAboutCustomerReducer = (
    state: string | null | undefined,
    action: UnknownAction,
): string | null => {
    if (isSetFullCartAction(action)) {
        return action.payload.commentAboutCustomer;
    }

    return state ?? null;
};

const getInitialState = (): CartState => ({
    summary: getCartSummaryInitialState(),
    items: [],
    itemsProductData: productsAdapter.getInitialState(),
    editedOrderItems: [],
    recipes: recipesAdapter.getInitialState(),
    promos: [],
    editOrder: getCartEditOrderInitialState(),
    cartLoading: getCartLoadingInitialState(),
    timeslot: getTimeslotInitialState(),
    sections: [],
    commentAboutCustomer: null,
    ecoupons: cartECouponAdapter.getInitialState(),
});

const combined = combineReducers({
    summary: cartSummaryReducer,
    items: cartItemsReducer,
    itemsProductData: cartItemsProductDataReducer,
    editedOrderItems: cartEditedOrderItemsReducer,
    promos: cartPromosReducer,
    recipes: cartRecipesReducer,
    sections: cartSectionsReducer,
    editOrder: cartEditOrderReducer,
    cartLoading: cartLoadingReducer,
    timeslot: cartTimeslotReducer,
    commentAboutCustomer: commentAboutCustomerReducer,
    ecoupons: cartECouponReducer,
});
export type CartState = ReturnType<typeof combined>;

const cartSlice = createSlice({
    name: 'cart',
    initialState: getInitialState(),
    reducers: {},
    extraReducers: (builder) => {
        builder.addDefaultCase((state, action) => combined(state, action));
    },
});

export default cartSlice.reducer;
